import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
// Icons removed - not used in this component
import { useAuth } from '../../context/AuthContext';
import insightsService from '../../services/insights.service';
import FeedbackDialog from '../ExecutionTester/components/FeedbackDialog';
import feedbackService from '../../services/feedback.service';
import { debugError } from '../../utils/debugLog';
import {
  useOperationalInsights,
  useTemplateProgressionInsights,
} from './hooks';
import {
  SummaryStatisticsCards,
  InsightSummaryCard,
  PreviousReportsDialog,
  InsightSection,
  TemplateEffectivenessSection,
} from './InsightsComponents';

/**
 * Insights Page Component
 *
 * Displays AI-generated insights with:
 * - Insight Agent summary (LLM-generated)
 * - Details section (grid of insight cards)
 * - Insight Agent Runs navigation
 */
const Insights = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0); // 0 = Operational, 1 = Template Progression

  // Previous reports dialog state
  const [previousReportsDialogOpen, setPreviousReportsDialogOpen] =
    useState(false);
  const [previousReportsType, setPreviousReportsType] = useState(null);

  // Feedback dialog state
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [newFeedback, setNewFeedback] = useState({ rating: 0, comment: '' });

  // Legacy state (kept for backward compatibility during migration)
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchInsights, setBatchInsights] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState(null);

  const { isAuthenticated, loading: authLoading } = useAuth();

  // Use extracted hooks for insights management
  const {
    operationalBatches,
    operationalBatchInsights,
    operationalLoading,
    selectedOperationalBatchId,
    setOperationalBatches,
    setSelectedOperationalBatchId,
    loadOperationalBatchInsights,
    loadLatestOperationalBatches,
  } = useOperationalInsights(isAuthenticated, authLoading);

  const {
    progressionBatches,
    progressionBatchInsights,
    progressionLoading,
    selectedProgressionBatchId,
    setProgressionBatches,
    setSelectedProgressionBatchId,
    loadProgressionBatchInsights,
    loadLatestProgressionBatches,
  } = useTemplateProgressionInsights(isAuthenticated, authLoading);

  // Fetch latest batches for each type using optimized endpoints
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadLatestOperationalBatches();
      loadLatestProgressionBatches();

      // Keep legacy batches for backward compatibility
      insightsService
        .listBatches({ limit: 10 })
        .then(response => {
          if (response && response.batches) {
            setBatches(response.batches);
            if (response.batches.length > 0) {
              setSelectedBatchId(response.batches[0].batch_id);
            }
          }
        })
        .catch(err => {
          debugError('Failed to load legacy batches:', err);
        });
    }
  }, [
    isAuthenticated,
    authLoading,
    loadLatestOperationalBatches,
    loadLatestProgressionBatches,
  ]);

  // Legacy function (kept for backward compatibility)
  const loadBatchInsights = async batchId => {
    if (!isAuthenticated || authLoading || !batchId) {
      return;
    }
    try {
      setBatchLoading(true);
      setBatchError(null);
      const response = await insightsService.getBatchInsights(batchId);
      setBatchInsights(response);
    } catch (err) {
      debugError('Failed to load batch insights:', err);
      setBatchError(err.message || 'Failed to load batch insights');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchChange = batchId => {
    setSelectedBatchId(batchId);
    loadBatchInsights(batchId);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenPreviousReports = async type => {
    setPreviousReportsType(type);
    setPreviousReportsDialogOpen(true);

    // Fetch all batches for the selected type (not just latest)
    try {
      if (type === 'operational') {
        const response = await insightsService.listBatches({ limit: 50 });
        if (response && response.batches) {
          const operationalBatchIds = response.batches.filter(batch => {
            const insightsByType = batch.insights_by_type || {};
            return (
              !insightsByType.template_version_comparison ||
              insightsByType.template_version_comparison === 0
            );
          });
          setOperationalBatches(operationalBatchIds);
        }
      } else if (type === 'progression') {
        const response = await insightsService.listBatches({
          insight_type: 'template_version_comparison',
          limit: 50,
        });
        if (response && response.batches) {
          setProgressionBatches(response.batches);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load previous reports:', err);
    }
  };

  const handleClosePreviousReports = () => {
    setPreviousReportsDialogOpen(false);
    setPreviousReportsType(null);
  };

  const handleSelectPreviousBatch = async (batchId, type) => {
    if (type === 'operational') {
      setSelectedOperationalBatchId(batchId);
      await loadOperationalBatchInsights(batchId);
    } else if (type === 'progression') {
      setSelectedProgressionBatchId(batchId);
      await loadProgressionBatchInsights(batchId);
    }
    handleClosePreviousReports();
  };

  const handleSubmitFeedback = async () => {
    // Get the appropriate batch based on active tab
    const currentBatchForFeedback =
      activeTab === 0 ? operationalBatches[0] : progressionBatches[0];

    if (!currentBatchForFeedback?.summary_execution_id) {
      debugError('No execution ID available for feedback');
      return;
    }

    try {
      await feedbackService.submitFeedback({
        execution_id: currentBatchForFeedback.summary_execution_id,
        rating: newFeedback.rating,
        feedback_text: newFeedback.comment || undefined,
      });
      setFeedbackDialogOpen(false);
      setNewFeedback({ rating: 0, comment: '' });
      // Show success message (you could add a snackbar here)
      alert('Feedback submitted successfully!');
    } catch (error) {
      debugError('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  // Legacy functions (kept for backward compatibility during migration)
  // eslint-disable-next-line no-unused-vars
  const handlePreviousBatch = () => {
    const currentIndex = batches.findIndex(b => b.batch_id === selectedBatchId);
    if (currentIndex > 0) {
      handleBatchChange(batches[currentIndex - 1].batch_id);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleNextBatch = () => {
    const currentIndex = batches.findIndex(b => b.batch_id === selectedBatchId);
    if (currentIndex < batches.length - 1) {
      handleBatchChange(batches[currentIndex + 1].batch_id);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const selectedBatch = batches.find(b => b.batch_id === selectedBatchId);
  // eslint-disable-next-line no-unused-vars
  const currentBatchIndex = batches.findIndex(
    b => b.batch_id === selectedBatchId
  );

  // Show loading if auth is still initializing
  if (authLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show message if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">Please log in to view insights.</Alert>
      </Container>
    );
  }

  // Period and severity are determined by the batch, not user-selectable
  // Users can switch between batches to see different time periods and severities

  if (batchLoading && !batchInsights) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading batch insights...</Typography>
        </Box>
      </Container>
    );
  }

  if (batchError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{batchError}</Alert>
      </Container>
    );
  }

  // Get current batch and insights based on active tab
  const currentBatch =
    activeTab === 0
      ? operationalBatches.find(
          b => b.batch_id === selectedOperationalBatchId
        ) || operationalBatches[0]
      : progressionBatches.find(
          b => b.batch_id === selectedProgressionBatchId
        ) || progressionBatches[0];
  const currentBatchInsights =
    activeTab === 0 ? operationalBatchInsights : progressionBatchInsights;
  const currentBatches =
    activeTab === 0 ? operationalBatches : progressionBatches;

  // Don't show "No batches available" if we're still loading
  if (!operationalLoading && !progressionLoading && !batchLoading) {
    if (
      currentBatches.length === 0 &&
      operationalBatches.length === 0 &&
      progressionBatches.length === 0
    ) {
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Alert severity="info">
            No insight batches available. Please generate insights first.
          </Alert>
        </Container>
      );
    }
  }

  if (
    !currentBatchInsights &&
    currentBatches.length > 0 &&
    !operationalLoading &&
    !progressionLoading
  ) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          Loading {activeTab === 0 ? 'operational' : 'template progression'}{' '}
          insights...
        </Alert>
      </Container>
    );
  }

  // Use current batch insights based on active tab
  const batchInsightsToUse = currentBatchInsights || batchInsights;

  // Use batch insights only - batchInsights structure: { batch: {...}, insights: [...] }
  // Insights from batch API have insight_data JSONB field that needs to be merged
  const displayInsights = batchInsightsToUse?.insights
    ? {
        template_effectiveness: batchInsightsToUse.insights
          .filter(i => i.insight_type === 'template_effectiveness')
          .map(i => ({
            ...i,
            ...(i.insight_data || {}), // Merge insight_data fields into the object
          })),
        error_trends: batchInsightsToUse.insights
          .filter(i => i.insight_type === 'error_trend')
          .map(i => ({
            ...i,
            ...(i.insight_data || {}),
          })),
        anomalies: batchInsightsToUse.insights
          .filter(i => i.insight_type === 'anomaly')
          .map(i => ({
            ...i,
            ...(i.insight_data || {}),
          })),
        system_trends: batchInsightsToUse.insights
          .filter(i => i.insight_type === 'system_trend')
          .map(i => ({
            ...i,
            ...(i.insight_data || {}),
          })),
        version_comparisons: batchInsightsToUse.insights
          .filter(i => i.insight_type === 'template_version_comparison')
          .map(i => ({
            ...i,
            ...(i.insight_data || {}),
          })),
        summary: {
          total_insights: batchInsightsToUse.batch?.total_insights || 0,
          template_effectiveness_count: batchInsightsToUse.insights.filter(
            i => i.insight_type === 'template_effectiveness'
          ).length,
          error_trend_count: batchInsightsToUse.insights.filter(
            i => i.insight_type === 'error_trend'
          ).length,
          anomaly_count: batchInsightsToUse.insights.filter(
            i => i.insight_type === 'anomaly'
          ).length,
          system_trend_count: batchInsightsToUse.insights.filter(
            i => i.insight_type === 'system_trend'
          ).length,
          version_comparison_count: batchInsightsToUse.insights.filter(
            i => i.insight_type === 'template_version_comparison'
          ).length,
        },
      }
    : null;

  const summary = displayInsights?.summary || {};
  const templateInsights = displayInsights?.template_effectiveness || [];
  const errorTrends = displayInsights?.error_trends || [];
  const anomalies = displayInsights?.anomalies || [];
  const systemTrends = displayInsights?.system_trends || [];

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 3,
        backgroundColor: '#F2F2F7',
        minHeight: '100vh',
      }}
    >
      {/* Header Section - iOS Flat Style */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#000000',
            mb: 0.5,
            fontSize: '1.5rem',
            letterSpacing: '-0.02em',
          }}
        >
          AI Insights
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#8E8E93',
            fontSize: '0.875rem',
            mb: 3,
          }}
        >
          AI-generated insights from execution analytics
        </Typography>

        {/* Summary Statistics Cards */}
        <SummaryStatisticsCards summary={summary} />

        {/* Insight Summary Tabs - iOS Flat Style */}
        <Card
          sx={{
            mb: 3,
            borderRadius: '10px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5EA',
            boxShadow: 'none',
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="insight summary tabs"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  minHeight: 48,
                  color: '#8E8E93',
                  '&.Mui-selected': {
                    color: '#007AFF',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#007AFF',
                },
              }}
            >
              <Tab label="Operational Insights" />
              <Tab label="Template Progression Insights" />
            </Tabs>
          </Box>

          {/* Operational Insights Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 0 }}>
              {operationalBatches.length > 0 && currentBatch && (
                <Box sx={{ p: 2.5, borderBottom: '1px solid #E5E5EA' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#8E8E93',
                        fontSize: '0.75rem',
                      }}
                    >
                      Latest Report • Period:{' '}
                      {new Date(
                        currentBatch.analysis_period_start
                      ).toLocaleDateString()}{' '}
                      -{' '}
                      {new Date(
                        currentBatch.analysis_period_end
                      ).toLocaleDateString()}
                    </Typography>
                    {operationalBatches.length > 1 && (
                      <Link
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          handleOpenPreviousReports('operational');
                        }}
                        sx={{
                          color: '#007AFF',
                          fontSize: '0.875rem',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        View Previous Reports ({operationalBatches.length - 1}{' '}
                        more)
                      </Link>
                    )}
                  </Box>
                </Box>
              )}
              <InsightSummaryCard
                batch={currentBatch}
                batches={operationalBatches}
                isProgression={false}
                onFeedbackClick={() => setFeedbackDialogOpen(true)}
                onViewPreviousReports={() =>
                  handleOpenPreviousReports('operational')
                }
              />
            </Box>
          )}

          {/* Template Progression Insights Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 0 }}>
              {progressionBatches.length > 0 && currentBatch && (
                <Box sx={{ p: 2.5, borderBottom: '1px solid #E5E5EA' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#8E8E93',
                        fontSize: '0.75rem',
                      }}
                    >
                      Latest Report • Period:{' '}
                      {new Date(
                        currentBatch.analysis_period_start
                      ).toLocaleDateString()}{' '}
                      -{' '}
                      {new Date(
                        currentBatch.analysis_period_end
                      ).toLocaleDateString()}
                    </Typography>
                    {progressionBatches.length > 1 && (
                      <Link
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          handleOpenPreviousReports('progression');
                        }}
                        sx={{
                          color: '#007AFF',
                          fontSize: '0.875rem',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        View Previous Reports ({progressionBatches.length - 1}{' '}
                        more)
                      </Link>
                    )}
                  </Box>
                </Box>
              )}
              <InsightSummaryCard
                batch={currentBatch}
                batches={progressionBatches}
                isProgression={true}
                onFeedbackClick={() => setFeedbackDialogOpen(true)}
                onViewPreviousReports={() =>
                  handleOpenPreviousReports('progression')
                }
              />
            </Box>
          )}
        </Card>

        {/* Feedback Dialog */}
        <FeedbackDialog
          open={feedbackDialogOpen}
          onClose={() => {
            setFeedbackDialogOpen(false);
            setNewFeedback({ rating: 0, comment: '' });
          }}
          newFeedback={newFeedback}
          setNewFeedback={setNewFeedback}
          onSubmitFeedback={handleSubmitFeedback}
          title="Add Feedback for Summary"
          ratingLabel="Rate this summary"
          placeholder="Share your thoughts on this insight summary..."
        />

        {/* Previous Reports Dialog */}
        <PreviousReportsDialog
          open={previousReportsDialogOpen}
          onClose={handleClosePreviousReports}
          reportType={previousReportsType}
          batches={
            previousReportsType === 'operational'
              ? operationalBatches
              : progressionBatches
          }
          selectedBatchId={
            previousReportsType === 'operational'
              ? selectedOperationalBatchId
              : selectedProgressionBatchId
          }
          onSelectBatch={handleSelectPreviousBatch}
        />

        {/* Period and severity are determined by the selected batch, not user-selectable */}
      </Box>

      {/* Details Section - iOS Flat Style */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: '#000000',
            mb: 2.5,
            fontSize: '1.125rem',
            letterSpacing: '-0.01em',
          }}
        >
          Details
        </Typography>

        <Grid
          container
          spacing={2.5}
          sx={{
            alignItems: 'stretch',
            width: '100%',
            '& > .MuiGrid-item': {
              display: 'flex',
              width: '100%',
            },
          }}
        >
          {/* System Trend Insights */}
          <InsightSection
            title="System Trend Insights"
            insights={systemTrends}
            type="system_trend"
          />

          {/* Template Effectiveness Insights */}
          <TemplateEffectivenessSection insights={templateInsights} />

          {/* Error Trend Insights */}
          <InsightSection
            title="Error Trend Insights"
            insights={errorTrends}
            type="error_trend"
          />

          {/* Anomaly Insights */}
          <InsightSection
            title="Anomaly Insights"
            insights={anomalies}
            type="anomaly"
          />

          {/* Loading Spinner or No Insights Message */}
          {(batchLoading || operationalLoading || progressionLoading) &&
          !batchInsightsToUse ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2, color: '#8E8E93' }}>
                  Loading insights...
                </Typography>
              </Box>
            </Grid>
          ) : (
            !batchLoading &&
            !operationalLoading &&
            !progressionLoading &&
            batchInsightsToUse &&
            templateInsights.length === 0 &&
            errorTrends.length === 0 &&
            anomalies.length === 0 &&
            systemTrends.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  No insights found in the selected batch.
                </Alert>
              </Grid>
            )
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default Insights;
